import React from 'react'
import { AppProps } from 'next/app'
import Head from 'next/head'
import { ThemeProvider } from 'next-themes'
import { TooltipProvider } from '@radix-ui/react-tooltip'
import { QueryClientProvider } from '@tanstack/react-query'
import { AuthStateProvider } from '../contexts/AuthContext'
import { queryClient } from 'lib/queryClient'
import { Toaster } from '../components/ui/sonner'
// Global CSS (fonts.css / globals.css / index.css) is imported by each app's
// pages/_app.tsx — Next.js only allows global CSS imports there.
// AG Grid styles moved to component-level imports (not needed for landing page)
// Only imported in pages that actually use AG Grid

// Import all components statically - dynamic imports were causing rendering issues in production
import ErrorBoundary from '../components/shared/ErrorBoundary'
import PreviewInteractionGuard from '../components/shared/PreviewInteractionGuard'
import AuthProvider from '../components/AuthProvider'
import { AnalyticsScripts } from '../components/shared/AnalyticsScripts'
import { CookieBanner } from '../components/shared/CookieBanner'
import { CookieConsentProvider } from '../contexts/CookieConsentContext'
import ModalScrollLockManager from '../components/shared/ModalScrollLockManager'
import FullPageLayout from '../layout/FullPageLayout'
import { AppRouterProvider } from '../contexts/AppRouterContext'
import {
  isNoAuthRedirectRoute,
  isAuthRoute,
  isProviderRoute,
  isSuperAdminRoute,
  isCustomerRoute,
  isSharedRoute,
} from '../config/routes.config'
import { ImpersonationHandoffGate } from '../components/shared/ImpersonationHandoffGate'

// Global error handlers
if (typeof window !== 'undefined') {
  // Disable Next.js error overlay in development
  if (process.env.NODE_ENV === 'development') {
    window.__NEXT_DATA__ = window.__NEXT_DATA__ || {}
    // @ts-expect-error - Setting err to null to disable error overlay
    window.__NEXT_DATA__.err = null

    // Suppress HMR WebSocket errors when using ngrok/tunneling
    // The HMR WebSocket can't connect through ngrok, causing repeated errors and potential reloads
    const isNgrok = window.location.hostname.includes('ngrok')
    if (isNgrok) {
      // Override console.error to filter out HMR WebSocket messages
      const originalError = console.error
      console.error = (...args) => {
        const message = args[0]?.toString?.() || ''
        // Filter out HMR/webpack WebSocket errors when using ngrok
        if (message.includes('webpack-hmr') || message.includes('WebSocket connection')) {
          return // Suppress these errors
        }
        originalError.apply(console, args)
      }

      // Intercept WebSocket to prevent HMR connections when using ngrok
      // This prevents the reconnection loop that can cause page reloads
      const OriginalWebSocket = window.WebSocket
      // @ts-expect-error - Override WebSocket constructor for ngrok compatibility
      window.WebSocket = function (url: string, protocols?: string | string[]) {
        // Block HMR WebSocket connections - they can't work through ngrok
        if (url.includes('webpack-hmr') || url.includes('_next/webpack')) {
          // Return a mock WebSocket that does nothing
          return {
            addEventListener: () => {},
            removeEventListener: () => {},
            close: () => {},
            send: () => {},
            readyState: 3, // CLOSED
            CONNECTING: 0,
            OPEN: 1,
            CLOSING: 2,
            CLOSED: 3,
          }
        }
        // Allow all other WebSocket connections (like socket.io for real-time updates)
        return new OriginalWebSocket(url, protocols)
      }
      // Copy static properties
      // @ts-expect-error - WebSocket static property
      window.WebSocket.CONNECTING = 0
      // @ts-expect-error - WebSocket static property
      window.WebSocket.OPEN = 1
      // @ts-expect-error - WebSocket static property
      window.WebSocket.CLOSING = 2
      // @ts-expect-error - WebSocket static property
      window.WebSocket.CLOSED = 3
    }
  }

  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', event => {
    console.error('Unhandled promise rejection:', event.reason)

    // Check if it's an API error with status code
    const error = event.reason
    const statusCode = error?.response?.status || error?.status

    if (statusCode === 401) {
      // Import route config dynamically to check if we should redirect
      import('../config/routes.config').then(({ isNoAuthRedirectRoute }) => {
        const currentPath = window.location.pathname

        // Check if we're on a route that should never redirect to login
        if (isNoAuthRedirectRoute(currentPath)) {
          console.error('401 error on no-auth-redirect route - not redirecting to login')
          return
        }

        // Check if we're already on the login page to prevent redirect loop
        if (currentPath === '/login' || currentPath === '/login/') {
          // Already on login page - just show a toast message
          console.error('401 error on login page - login failed')
        } else {
          // Don't clear localStorage - just redirect to login with return URL
          // The token should only be cleared on explicit logout
          const fullPath = window.location.pathname + window.location.search
          const loginUrl = `/login?returnUrl=${encodeURIComponent(fullPath)}`
          window.location.href = loginUrl
        }
      })
    } else if (statusCode === 403) {
      // For 403 errors, don't remove token - user might be able to reload
      // Just show the error and let the user decide what to do
      console.error('403 error - access denied, but keeping token for potential reload')
      // Don't prevent default - let the component handle the error
      return
    } else if (statusCode === 500) {
      // Redirect to error page for 500 errors
      const errorMessage = error?.response?.data?.message || error?.message || 'Server error'
      window.location.href = `/error?message=${encodeURIComponent(errorMessage)}`
    } else if (statusCode === 400 || statusCode === 404) {
      // Don't redirect for 400/404 errors - let components handle them gracefully
      console.warn(
        '400/404 error handled by component:',
        error?.response?.data?.message || error?.message,
      )
    } else if (statusCode) {
      // For other HTTP errors, redirect to error page
      const errorMessage =
        error?.response?.data?.message || error?.message || 'An unexpected error occurred'
      window.location.href = `/error?message=${encodeURIComponent(errorMessage)}`
    } else {
      // Non-HTTP errors (e.g. WebSocket transport failures, network blips)
      // -- log but don't redirect. These are transient and handled by
      // their own retry/reconnection logic.
      console.warn('Non-HTTP unhandled rejection (ignored):', error?.message || error)
    }

    // Prevent default for errors we've handled (critical HTTP + non-HTTP)
    event.preventDefault()
  })

  // Handle global errors - log but don't redirect (let ErrorBoundary handle UI)
  window.addEventListener('error', event => {
    const errorMessage = event.error?.message || 'An unexpected error occurred'
    const errorName = event.error?.name || 'Error'

    console.error('[GlobalErrorHandler] Caught error:', {
      name: errorName,
      message: errorMessage,
      stack: event.error?.stack,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    })

    // Don't redirect for runtime errors - they should be handled by ErrorBoundary
    // or individual components. Only log for debugging.
    // This prevents the app from crashing to error page for minor issues.

    // Prevent the error from propagating to the browser's default handler
    // to avoid duplicate error handling
    event.preventDefault()
  })
}

/**
 * Surface-specific layouts are injected per app (each app owns its own
 * layout; the shell and route branching stay shared and unchanged).
 * Branches whose layout is not supplied can never match in that app,
 * so they default to the shared FullPageLayout.
 */
export interface AppLayouts {
  ProviderLayout?: React.ComponentType<{ children: React.ReactNode }>
  OperationLayout?: React.ComponentType<{ children: React.ReactNode }>
  CustomerLayout?: React.ComponentType<{ children: React.ReactNode }>
}

export function createApp(layouts: AppLayouts = {}) {
  const ProviderLayout = layouts.ProviderLayout ?? FullPageLayout
  const OperationLayout = layouts.OperationLayout ?? FullPageLayout
  const CustomerLayout = layouts.CustomerLayout ?? FullPageLayout

  function AppContent({ Component, pageProps, router }: AppProps) {
    // Use router from AppProps — useRouter() throws during static prerender (e.g. /404).
    const pathname = router.pathname

    // Check route types using centralized config
    const isNoAuthRedirect = isNoAuthRedirectRoute(pathname)
    const isAuth = isAuthRoute(pathname)
    const isProvider = isProviderRoute(pathname)
    const isSuperAdmin = isSuperAdminRoute(pathname)
    const isCustomer = isCustomerRoute(pathname)
    const isShared = isSharedRoute(pathname)

    // Special cases for checkout and manage pages (no layout needed)
    const isCheckoutPage =
      pathname === '/customer/checkout' || pathname === '/customer/checkout/success'
    const isCustomerManagePage =
      pathname === '/customer/manage' || pathname.startsWith('/customer/manage/')
    // Hosted OAuth customer pages (should render without the app shell/layout).
    // These are embedded in the provider "Pages" preview iframe and are also used
    // as standalone hosted pages, so avoid `FullPageLayout`'s header + 80vh wrapper.
    const isHostedOAuthLoginPage = pathname === '/auth/login'
    const isHostedOAuthCallbackPage = pathname === '/auth/callback/customer'
    const isHostedOAuthConsentPage = pathname === '/auth/customer/consent'
    const isProviderOnboarding =
      pathname === '/provider/register' || pathname.startsWith('/provider/onboarding/')
    const isMcpConnect = pathname === '/auth/mcp-connect'

    return (
      <>
        {isMcpConnect ? (
          <AuthProvider key="mcp-connect">
            <FullPageLayout headerLogoOnly>
              <Component {...pageProps} />
            </FullPageLayout>
          </AuthProvider>
        ) : isProviderOnboarding ? (
          <AuthProvider key="provider-onboarding">
            <FullPageLayout>
              <Component {...pageProps} />
            </FullPageLayout>
          </AuthProvider>
        ) : isCheckoutPage ? (
          // Checkout page - no layout, no AuthProvider (banned from authenticated endpoints)
          <Component {...pageProps} />
        ) : isCustomerManagePage ? (
          // Customer manage page - no layout, uses custom full-screen styling
          <Component {...pageProps} />
        ) : isHostedOAuthLoginPage ? (
          // Hosted OAuth login pages - no layout, no AuthProvider
          <Component {...pageProps} />
        ) : isHostedOAuthCallbackPage ? (
          // Hosted OAuth callback page - no layout, no AuthProvider (has its own BrandedAuthLayout)
          <Component {...pageProps} />
        ) : isHostedOAuthConsentPage ? (
          // Hosted OAuth consent page - no layout, no AuthProvider (has its own BrandedAuthLayout)
          <Component {...pageProps} />
        ) : isNoAuthRedirect ? (
          // OAuth flows, callbacks, and other pages that should never redirect to login
          // These pages handle their own authentication flow
          <FullPageLayout>
            <Component {...pageProps} />
          </FullPageLayout>
        ) : isProvider ? (
          <AuthProvider key="provider">
            <ProviderLayout>
              <Component {...pageProps} />
            </ProviderLayout>
          </AuthProvider>
        ) : isSuperAdmin ? (
          <AuthProvider>
            <OperationLayout>
              <Component {...pageProps} />
            </OperationLayout>
          </AuthProvider>
        ) : isCustomer ? (
          <AuthProvider>
            <CustomerLayout>
              <Component {...pageProps} />
            </CustomerLayout>
          </AuthProvider>
        ) : isAuth ? (
          <FullPageLayout>
            <Component {...pageProps} />
          </FullPageLayout>
        ) : isShared ? (
          <AuthProvider>
            <FullPageLayout>
              <Component {...pageProps} />
            </FullPageLayout>
          </AuthProvider>
        ) : (
          <AuthProvider>
            <FullPageLayout>
              <Component {...pageProps} />
            </FullPageLayout>
          </AuthProvider>
        )}
      </>
    )
  }

  return function App(props: AppProps) {
    return (
      <AuthStateProvider>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider
            attribute="class"
            defaultTheme="system"
            enableSystem
            disableTransitionOnChange={false}
          >
            <TooltipProvider delayDuration={300}>
              <Head>
                <title>SolvaPay - Agentic Payments</title>
                <meta
                  name="description"
                  content="Payment infrastructure for AI agents, MCP tools, and APIs. Integrate conversational checkout, usage-based billing, and purchase management with comprehensive SDK documentation."
                />
                <meta name="viewport" content="width=device-width, initial-scale=1" />

                {/* Open Graph */}
                <meta property="og:title" content="SolvaPay - Agentic Payments" />
                <meta
                  property="og:description"
                  content="Payment infrastructure for AI agents, MCP tools, and APIs. Integrate conversational checkout and usage-based billing with comprehensive SDK documentation."
                />
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://solvapay.com" />
                <meta property="og:site_name" content="SolvaPay" />

                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="SolvaPay - Agentic Payments" />
                <meta
                  name="twitter:description"
                  content="Payment infrastructure for AI agents with comprehensive SDK documentation."
                />

                {/* Favicon */}
                <link rel="icon" href="/favicon.ico" />
              </Head>

              <CookieConsentProvider>
                <AppRouterProvider router={props.router}>
                  <ErrorBoundary>
                    <PreviewInteractionGuard router={props.router} />
                    <ModalScrollLockManager />
                    <AppContent {...props} />
                    <CookieBanner />
                  </ErrorBoundary>
                </AppRouterProvider>

                <AnalyticsScripts />
                <Toaster />
              </CookieConsentProvider>
            </TooltipProvider>
          </ThemeProvider>
        </QueryClientProvider>
      </AuthStateProvider>
    )
  }
}
